/**
 * The network of a WorkWise request in step 3 and on Review (revisions 3 and 4).
 *  - Dedicated: the derived rg-/vnet-/snet-<region>-<env>-ww-<customer> names, read-only like the
 *    backend computes them, each next to the address range (CIDR) the user types for it (R4-A).
 *  - SaaS-Hybrid: the "App server network" select. Its default is the picked shared DB server's
 *    network, with a warning (that does not block) while that server has none recorded; the other
 *    options are the existing networks recorded in Django admin for the region and environment (R4-B).
 * Existing classes only (lbInfoBanner, vnetInfoBox, formGrid, review rows).
 */
import type { ReactNode } from 'react'
import { TriangleAlert } from 'lucide-react'
import { useFormContext, useWatch } from 'react-hook-form'
import { ENVIRONMENT_LABELS, EXISTING_NETWORK_ADMIN_PATH, SHARED_SERVER_ADMIN_PATH } from '@/config/workwise'
import { useExistingNetworks, useSharedPicks } from '@/hooks/useWorkwise'
import styles from '@/pages/NewDeploymentPage.module.css'
import type { WorkwiseFormValues } from '@/types/workwise'
import { type ResolvedNetwork, existingNetworkLabel, formNetwork, sharedServerNetwork } from '@/utils/workwise/model'
import { InfoField, Row, Section, SelectField, TextField } from './fields'

/** "Network not recorded for <server> yet; ...". Informational: Next and Deploy stay enabled. */
export function PendingNetworkNotice({ serverName, testId }: { serverName: string; testId: string }) {
  return (
    <div
      className={styles.lbInfoBanner}
      role="status"
      data-testid={testId}
      style={{ marginTop: 0, marginBottom: 16, borderColor: 'var(--aptean-warning)' }}
    >
      <TriangleAlert size={13} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px', color: 'var(--aptean-warning)' }} />
      Network not recorded for <strong>{serverName}</strong> yet; the request is created with the network pending (fill it in{' '}
      {SHARED_SERVER_ADMIN_PATH}).
    </div>
  )
}

function emptyText(network: ResolvedNetwork): string {
  if (network.source === 'customer') return 'Set from the customer name'
  if (network.source === 'existing_network') return 'Not recorded'
  return network.pending ? 'Pending' : 'Select a shared database server'
}

function sourceHint(network: ResolvedNetwork): string {
  if (network.source === 'customer') {
    return "Dedicated: the customer's own resource group, VNet and subnet, named <type>-<region>-<env>-ww-<customer> (customer name in lower case, without spaces or symbols), with the address ranges entered here. All three VMs use it."
  }
  if (network.existingNetwork) {
    return `SaaS-Hybrid: the app server joins the existing network ${network.existingNetwork.name} instead of the shared database server's network.`
  }
  return network.serverName
    ? `SaaS-Hybrid: the network recorded on the shared database server ${network.serverName}; the app server joins it.`
    : 'SaaS-Hybrid: the network comes from the shared database server picked above.'
}

/** SaaS-Hybrid: the shared DB server's network (default) or one of the existing networks of the region + environment. */
function AppServerNetworkField({ values }: { values: WorkwiseFormValues }) {
  const picks = useSharedPicks(values)
  const query = useExistingNetworks(values.region, values.environment)
  const networks = query.data
  const shared = sharedServerNetwork(picks.db)
  const sharedLabel = shared.serverName ? `Shared database server's network — ${shared.vnet || 'not recorded'}` : "Shared database server's network"
  const where = `${ENVIRONMENT_LABELS[values.environment] ?? values.environment} ${values.region}`
  let hint: ReactNode
  if (query.isError) {
    hint = (
      <>
        Could not load the existing networks.{' '}
        <button type="button" className={styles.addTagBtn} onClick={() => void query.refetch()}>
          Retry
        </button>
      </>
    )
  } else if (networks && networks.length === 0) {
    hint = (
      <span data-testid="ww-existing-networks-empty">
        No existing networks recorded for {where}; add them in {EXISTING_NETWORK_ADMIN_PATH}.
      </span>
    )
  } else if (networks) {
    const lead = picks.network ? 'An existing network' : 'Or join an existing network'
    hint = <span data-testid="ww-existing-networks-hint">{lead} recorded for {where} in {EXISTING_NETWORK_ADMIN_PATH}.</span>
  } else {
    hint = <span data-testid="ww-existing-networks-loading">Loading existing networks…</span>
  }
  const options = [
    { value: '', label: sharedLabel },
    ...(networks ?? []).map((n) => ({ value: String(n.id), label: existingNetworkLabel(n) })),
  ]
  return <SelectField path="network.existingNetworkId" label="App server network" options={options} hint={hint} asNumber />
}

/** Step 3: the network as derived values, plus the Dedicated address ranges or the SaaS-Hybrid network choice. */
export function NetworkSection() {
  const { control } = useFormContext<WorkwiseFormValues>()
  const values = useWatch({ control }) as WorkwiseFormValues
  const network = formNetwork(values, useSharedPicks(values))
  const empty = emptyText(network)
  const dedicated = values.deploymentModel === 'dedicated'
  const rg = <InfoField label="Resource Group" value={network.resourceGroup} empty={empty} testId="ww-network-rg" />
  const vnet = <InfoField label="VNet" value={network.vnet} empty={empty} testId="ww-network-vnet" />
  const subnet = <InfoField label="Subnet" value={network.subnet} empty={empty} testId="ww-network-subnet" />
  return (
    <Section title="Network" testId="ww-network">
      {network.pending && network.serverName && <PendingNetworkNotice serverName={network.serverName} testId="ww-network-pending" />}
      {dedicated ? (
        <div className={styles.formGrid}>
          <div style={{ gridColumn: '1 / -1' }}>{rg}</div>
          {vnet}
          <TextField
            path="network.vnetCidr"
            label="VNet address space (CIDR)"
            required
            placeholder="10.20.0.0/24"
            hint="Private range (10.x, 172.16–31.x or 192.168.x), /16 to /29; must not overlap another WorkWise network in this environment"
          />
          {subnet}
          <TextField path="network.subnetCidr" label="Subnet address prefix (CIDR)" required placeholder="10.20.0.0/27" hint="Inside the VNet range, /16 to /29" />
        </div>
      ) : (
        <>
          <AppServerNetworkField values={values} />
          <div className={styles.formGrid} style={{ marginTop: 16 }}>
            {rg}
            {vnet}
            {subnet}
          </div>
        </>
      )}
      <div className={styles.fieldHint} data-testid="ww-network-source">
        {sourceHint(network)}
      </div>
    </Section>
  )
}

/** Review: the same network as rows, full width inside the review grid. */
export function NetworkReview({ network }: { network: ResolvedNetwork }) {
  const show = (value: string) => value || (network.pending ? 'pending' : '—')
  const dedicated = network.source === 'customer'
  let source = '—'
  if (dedicated) source = 'Own network (Dedicated)'
  else if (network.existingNetwork) source = `Existing network ${network.existingNetwork.name}`
  else if (network.serverName) source = `Shared database server ${network.serverName}`
  return (
    <div className={styles.reviewSection} style={{ gridColumn: '1/-1' }} data-testid="ww-review-network">
      <h3 className={styles.reviewSectionTitle}>WorkWise Network</h3>
      {network.pending && network.serverName && <PendingNetworkNotice serverName={network.serverName} testId="ww-review-network-pending" />}
      <div className={styles.reviewTable}>
        <Row label="Source" value={source} testId="ww-review-network-source" />
        <Row label="Resource group" value={show(network.resourceGroup)} mono testId="ww-review-network-rg" />
        <Row label="VNet" value={show(network.vnet)} mono testId="ww-review-network-vnet" />
        {dedicated && <Row label="VNet address space" value={network.vnetCidr || '—'} mono testId="ww-review-network-vnet-cidr" />}
        <Row label="Subnet" value={show(network.subnet)} mono testId="ww-review-network-subnet" />
        {dedicated && <Row label="Subnet address prefix" value={network.subnetCidr || '—'} mono testId="ww-review-network-subnet-cidr" />}
        {network.existingNetwork?.addressSpace && (
          <Row label="Address space" value={network.existingNetwork.addressSpace} mono testId="ww-review-network-address-space" />
        )}
      </div>
    </div>
  )
}
